'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaOpencart } from 'react-icons/fa';
import './flyToCart.scss';

function getCartIconRect(target) {
  if (!target) {
    return null;
  }

  const icon = target.querySelector('.cart-icon');

  if (!(icon instanceof HTMLElement)) {
    return null;
  }

  const rect = icon.getBoundingClientRect();

  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }

  return rect;
}

function getVisibleCartAnchor() {
  const candidates = Array.from(
    document.querySelectorAll('[data-cart-anchor="true"]'),
  );

  return (
    candidates.find((candidate) => {
      const rect = candidate.getBoundingClientRect();
      const styles = window.getComputedStyle(candidate);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    }) ||
    candidates[0] ||
    null
  );
}

function getTargetPosition(target) {
  if (!target) {
    return {
      x: Math.max(window.innerWidth - 64, window.innerWidth - 120),
      y: 20,
    };
  }

  const rect = getCartIconRect(target) || target.getBoundingClientRect();
  const isSmallViewport = window.innerWidth < 768;
  const isLargeViewport = window.innerWidth >= 992;
  const responsiveOffset = {
    x: isSmallViewport ? 1 : isLargeViewport ? 2 : 0,
    y: isSmallViewport ? 4 : isLargeViewport ? 2 : 3,
  };

  return {
    x: rect.left + rect.width / 2 + responsiveOffset.x,
    y: rect.top + rect.height / 2 + responsiveOffset.y,
  };
}

export default function FlyToCart({
  startRect,
  onFinish,
  reducedMotion = false,
}) {
  const [mounted, setMounted] = useState(false);
  const [targetPosition, setTargetPosition] = useState(null);

  useEffect(() => {
    if (!startRect) {
      onFinish?.();
      return undefined;
    }

    const target = getVisibleCartAnchor();

    setTargetPosition(getTargetPosition(target));

    const frameId = window.requestAnimationFrame(() => setMounted(true));

    if (reducedMotion) {
      const timeoutId = window.setTimeout(() => onFinish?.(), 60);

      return () => {
        window.cancelAnimationFrame(frameId);
        window.clearTimeout(timeoutId);
      };
    }

    const timeoutId = window.setTimeout(() => onFinish?.(), 700);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [onFinish, reducedMotion, startRect]);

  if (!startRect || !targetPosition || typeof document === 'undefined') {
    return null;
  }

  const startX = startRect.left + startRect.width / 2;
  const startY = startRect.top + startRect.height / 2;
  const style = {
    left: `${startX}px`,
    top: `${startY}px`,
    transform: mounted
      ? `translate(${targetPosition.x - startX}px, ${targetPosition.y - startY}px) scale(0.45)`
      : 'translate(0, 0) scale(1)',
    opacity: mounted ? 0 : 1,
  };

  return createPortal(
    <div className="fly-to-cart" style={style} aria-hidden="true">
      <FaOpencart size={28} aria-hidden="true" />
    </div>,
    document.body,
  );
}
